/**
 * VersionWatcher
 * --------------
 *
 * Polls ``/version.json`` periodically and surfaces a non-intrusive
 * "Update available — Reload" banner when the deployed bundle is
 * newer than the one currently loaded in the user's tab.
 *
 * Why this exists: every previous deploy required users to know to
 * hard-refresh their tab. Users who didn't kept running the OLD JS
 * — that's the root cause of the recurring "I logged out and it
 * logged me right back in" reports. The new logout code shipped
 * fine, but the user's tab still held the old broken version, so
 * the fix was effectively invisible to them.
 *
 * How it works:
 *
 *  1. At build time, Vite emits ``dist/version.json`` and stamps a
 *     ``<meta name="app-version">`` into ``index.html``. Both carry
 *     the same git SHA (or build timestamp on dev). See
 *     ``configs/vite.base.config.ts`` → ``versionStampPlugin``.
 *
 *  2. At runtime, this component reads the meta tag once on mount
 *     to capture the loaded version, then polls the JSON endpoint
 *     every ``pollIntervalMs`` (default 60s) with ``cache: 'no-store'``
 *     so it never sees a CDN/browser-cached copy.
 *
 *  3. When the JSON's ``version`` differs from the loaded version,
 *     state flips to ``updateAvailable=true`` and a slide-in toast
 *     pops up bottom-centre with a Reload button.
 *
 *  4. Reload calls ``window.location.reload()``. Combined with the
 *     server's ``Cache-Control: no-cache`` header on ``index.html``
 *     this guarantees the new bundle is fetched.
 *
 * Safety:
 *
 *  - Silently no-ops if the meta tag or version.json is missing
 *    (e.g. in legacy bundles or dev mode without the plugin).
 *  - Skips polling when the tab is hidden (saves battery + cuts
 *    backend noise) and runs an immediate check when it becomes
 *    visible again, so a returning user gets the banner instantly.
 *  - Auto-reloads after ``autoReloadAfterMs`` of inactivity (no key
 *    presses, no mouse movement) when an update is pending. Defaults
 *    to ``null`` (manual click only) — opt-in by the host app if
 *    they want background self-healing.
 */

import { useEffect, useRef, useState } from 'react'
import { Check, RefreshCw, Tag, X } from 'lucide-react'

import {
  INITIAL_DISMISSAL_STATE,
  applyDismissal,
  processVersionPoll,
  type DismissalState,
} from './dismissalState'
import {
  INITIAL_COUNTDOWN_STATE,
  cancelCountdown,
  formatCountdownLabel,
  resetCountdown,
  shouldReloadOnVisibilityRegain,
  startCountdown,
  tickCountdown,
  type CountdownState,
} from './reloadPolicy'

/**
 * Window event fired ONCE per session when the watcher first observes
 * a deploy newer than the loaded bundle. Detail carries the version,
 * built-at timestamp, optional changelog payload from version.json,
 * and the loaded version we're upgrading FROM. Lets the host app
 * (notification bell, deploy-announce bridge, custom telemetry) react
 * without each one polling /version.json independently.
 */
export const QUOTECH_VERSION_AVAILABLE_EVENT = 'quotech.versionAvailable'

export interface VersionAvailableDetail {
  loadedVersion: string
  nextVersion: string
  nextBuiltAt: string | null
  /** Best-effort changelog payload — populated when version.json carries
   *  a `changes: [{sha, subject, author, date}]` array (SAM's bundle).
   *  Empty array if absent. */
  changes: Array<{ sha?: string; subject?: string; author?: string; date?: string }>
  /** Best-effort app label, derived from version.json `name` field. */
  name: string | null
}

export interface VersionWatcherProps {
  /** How often to check for a new version. Default: 60_000 (60s). */
  pollIntervalMs?: number
  /**
   * Fully-qualified URL or relative path to the version manifest.
   * Default: ``/version.json`` (or, if the SPA lives under a
   * sub-path like ``/company/sam/``, that prefix is auto-detected
   * from the loaded script tag).
   */
  versionUrl?: string
  /**
   * If set, auto-reload the page after this many ms of user
   * inactivity once an update is pending. Default ``null`` (manual
   * Reload click only). Set e.g. 10 * 60_000 to auto-reload after
   * 10 minutes of no input.
   */
  autoReloadAfterMs?: number | null
  /**
   * If true, console.info diagnostic messages on each poll. Useful
   * during initial rollout to verify the wiring. Default false.
   */
  debug?: boolean
  /**
   * Optional callback fired once per session when a new version is
   * detected. Receives the same detail as the
   * ``quotech.versionAvailable`` window event. Use this when the
   * host app prefers a React-style callback over a window event
   * listener (e.g. to push into a Zustand store).
   */
  onUpdateDetected?: (detail: VersionAvailableDetail) => void
  /**
   * If false, suppress the built-in floating "Reload now" pill —
   * useful when the host app already surfaces the update prompt
   * elsewhere (e.g. a notification bell entry with its own CTA)
   * and the floating banner would be redundant. Default ``true``.
   */
  showFloatingBanner?: boolean
  /**
   * If true, render a tiny build-version chip in the bottom-right
   * corner that ALWAYS shows the loaded version (e.g. for support
   * to ask "what build are you on?" without DevTools). Click → copy
   * the full version string to clipboard. The chip is intentionally
   * unobtrusive (text-[10px], 60% opacity, hover to 100%) and lives
   * in the opposite corner from the floating "Reload now" banner so
   * the two never overlap. Default ``false`` so existing hosts keep
   * the same UI; opt-in by SAM AppShell + tenant scaffolds.
   */
  showVersionBadge?: boolean
  /**
   * If set, when the operator returns to a hidden tab AND we detect
   * a pending update AND the tab was hidden for at least this many
   * ms, silently reload immediately (no toast click required). The
   * "they came back fresh" moment is the friendliest place in the
   * session to swap bundles — they expect new content anyway.
   * Default ``null`` (off). SAM uses 2 * 60_000 (2 min).
   */
  visibilityReloadAfterMs?: number | null
  /**
   * If set, when a new version is detected the toast displays a
   * "Reloading in Ns" countdown and auto-reloads after this many
   * ms unless the operator clicks Cancel. Cancellable per-version
   * — if a SECOND deploy lands later, a fresh countdown starts
   * (resetCountdown is called on brand-new detections to clear the
   * cancel flag). Default ``null`` (no countdown — manual click).
   * SAM uses 30_000 (30 s) so most operators never have to click.
   */
  autoReloadCountdownMs?: number | null
  /**
   * Distance in CSS px from the bottom of the viewport at which the
   * version badge anchors. Default 8 (matches the historic
   * ``bottom-2`` Tailwind class). Tenants with a fixed/sticky
   * bottom toolbar (e.g. SAM's Kanban view-switcher footer ~48 px
   * tall) should pass a larger offset so the badge clears the
   * toolbar instead of overlapping it (operator complaint
   * 2026-05-08: "the board ID is covering the bottom nav").
   */
  bottomOffsetPx?: number
  /**
   * Same as ``bottomOffsetPx`` but for the right-edge anchor.
   * Default 8. Tenants with a right-side rail (chat dock,
   * notification drawer, etc) that overlaps the historic
   * ``right-2`` position should bump this.
   */
  rightOffsetPx?: number
}

const META_VERSION = 'app-version'
const META_BUILT_AT = 'app-built-at'

function readMeta(name: string): string | null {
  if (typeof document === 'undefined') return null
  const tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  return tag?.content?.trim() || null
}

function defaultVersionUrl(): string {
  // If index.html was served from /company/sam/ (or /sydney-architectural-metal/
  // etc.) Vite emits version.json into the SAME prefix. We derive the
  // prefix from any <script src> that points at /<prefix>/assets/...
  if (typeof document === 'undefined') return '/version.json'
  const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[]
  for (const s of scripts) {
    const m = s.src.match(/\/([^/]+(?:\/[^/]+)?)\/assets\//)
    if (m) {
      // Match returns the prefix between the first ``/`` and ``/assets/``.
      // Use the URL constructor to keep the origin so authenticated
      // sub-paths still resolve correctly.
      try {
        const url = new URL(s.src)
        const prefix = url.pathname.split('/assets/')[0]
        return `${url.origin}${prefix}/version.json`
      } catch {
        // fall through to default
      }
    }
  }
  return '/version.json'
}

export function VersionWatcher({
  pollIntervalMs = 60_000,
  versionUrl,
  autoReloadAfterMs = null,
  debug = false,
  onUpdateDetected,
  showFloatingBanner = true,
  showVersionBadge = false,
  visibilityReloadAfterMs = null,
  autoReloadCountdownMs = null,
  bottomOffsetPx = 8,
  rightOffsetPx = 8,
}: VersionWatcherProps = {}) {
  // Single combined update + dismissal state. Wrapping the three
  // related fields in one object lets us swap the whole thing
  // atomically through the `processVersionPoll` helper, which is
  // critical for the "new deploy resets dismissal" semantics —
  // without it we'd race two `setX` calls on different ticks and
  // miss the reset window.
  const [vState, setVState] = useState<DismissalState>(INITIAL_DISMISSAL_STATE)
  const vStateRef = useRef<DismissalState>(INITIAL_DISMISSAL_STATE)
  useEffect(() => { vStateRef.current = vState }, [vState])
  // Mirror of loadedVersionRef into state so the badge renders the loaded
  // version on first paint AFTER the meta tag has been read in the
  // initialisation effect (refs do not trigger re-renders).
  const [loadedVersion, setLoadedVersion] = useState<string | null>(null)
  const [loadedBuiltAt, setLoadedBuiltAt] = useState<string | null>(null)
  const [badgeCopied, setBadgeCopied] = useState(false)
  // Countdown state: when non-null `remainingMs` we render a
  // "Reloading in Ns" pill and auto-reload at zero. `cancelled`
  // sticks until a brand-new version is detected (mirrors the
  // dismissal-reset semantics in DismissalState).
  const [countdown, setCountdown] = useState<CountdownState>(INITIAL_COUNTDOWN_STATE)
  const countdownRef = useRef<CountdownState>(INITIAL_COUNTDOWN_STATE)
  useEffect(() => { countdownRef.current = countdown }, [countdown])
  const loadedVersionRef = useRef<string | null>(null)
  const urlRef = useRef<string>('')
  const lastInputAtRef = useRef<number>(Date.now())
  // Timestamp (Date.now ms) when the tab last transitioned to
  // hidden — used by the visibility-regain reload policy. Null
  // means the tab is currently visible (or has never been hidden
  // in this mount).
  const hiddenSinceRef = useRef<number | null>(null)
  // Track which detected version's countdown we've already started
  // so we don't restart the timer on every 30s poll.
  const countdownStartedForRef = useRef<string | null>(null)
  const onUpdateDetectedRef = useRef(onUpdateDetected)
  useEffect(() => { onUpdateDetectedRef.current = onUpdateDetected }, [onUpdateDetected])

  // Initialise once.
  useEffect(() => {
    const v = readMeta(META_VERSION)
    const b = readMeta(META_BUILT_AT)
    loadedVersionRef.current = v
    setLoadedVersion(v)
    setLoadedBuiltAt(b)
    urlRef.current = versionUrl || defaultVersionUrl()
    if (debug) {
      console.info('[VersionWatcher] loaded version:', v,
        '| polling URL:', urlRef.current)
    }
  }, [versionUrl, debug])

  // Polling loop.
  useEffect(() => {
    if (typeof window === 'undefined') return
    let cancelled = false
    let timer: number | null = null

    const check = async () => {
      if (cancelled) return
      const loaded = loadedVersionRef.current
      if (!loaded) return
      try {
        const res = await fetch(urlRef.current, {
          cache: 'no-store',
          credentials: 'omit',
        })
        if (!res.ok) {
          if (debug) console.info('[VersionWatcher] poll non-OK:', res.status)
          return
        }
        const payload = (await res.json()) as {
          version?: string
          builtAt?: string
          name?: string
          changes?: Array<{ sha?: string; subject?: string; author?: string; date?: string }>
        }
        if (!payload?.version) return

        const { next, shouldAnnounce } = processVersionPoll(vStateRef.current, {
          loadedVersion: loaded,
          deployed: {
            nextVersion: payload.version,
            nextBuiltAt: payload.builtAt || null,
          },
        })
        if (next !== vStateRef.current) {
          if (debug && next.updateAvailable) {
            console.info('[VersionWatcher] update detected', {
              loaded, deployed: payload.version, dismissedReset: !next.dismissed,
            })
          }
          setVState(next)
        }
        if (shouldAnnounce) {
          // Brand-new version → clear any "Cancel" the operator
          // applied to the previous version's countdown so this
          // new deploy starts its own fresh countdown. Mirrors the
          // dismissed-reset behaviour in processVersionPoll.
          countdownStartedForRef.current = null
          setCountdown((prev) => resetCountdown(prev))
          const detail: VersionAvailableDetail = {
            loadedVersion: loaded,
            nextVersion: payload.version,
            nextBuiltAt: payload.builtAt || null,
            changes: Array.isArray(payload.changes) ? payload.changes : [],
            name: payload.name || null,
          }
          try {
            onUpdateDetectedRef.current?.(detail)
          } catch (cbErr) {
            if (debug) console.warn('[VersionWatcher] onUpdateDetected threw', cbErr)
          }
          try {
            window.dispatchEvent(
              new CustomEvent(QUOTECH_VERSION_AVAILABLE_EVENT, { detail }),
            )
          } catch {
            /* ignore — older browsers without CustomEvent ctor */
          }
        }
      } catch (err) {
        if (debug) console.info('[VersionWatcher] poll error', err)
      }
    }

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === 'visible') {
          await check()
        }
        if (!cancelled) schedule()
      }, pollIntervalMs)
    }

    // Run once on mount (avoid waiting a full poll interval).
    void check()
    schedule()

    // Refresh immediately when the user returns to the tab — closes
    // the worst-case window where someone leaves the tab open
    // overnight, we deploy at 8 AM, and they come back at 9 AM and
    // the next poll isn't due until 8:59 PM the previous day.
    //
    // ALSO: if `visibilityReloadAfterMs` is configured AND the tab
    // was hidden for at least that long AND we have a pending
    // update, just reload silently — the operator came back to a
    // fresh-feeling tab and that's the friendliest moment to swap
    // the bundle (no toast click required). Operator complaint
    // 2026-05-08: "why do I have to hard refresh, this is user
    // unfriendly".
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now()
        return
      }
      if (document.visibilityState === 'visible') {
        const hiddenSince = hiddenSinceRef.current
        hiddenSinceRef.current = null
        const awayMs = hiddenSince == null ? 0 : Date.now() - hiddenSince
        // Refresh first so the state reflects what's currently
        // deployed, then make the silent-reload decision.
        void check().then(() => {
          if (cancelled) return
          if (!vStateRef.current.updateAvailable) return
          if (vStateRef.current.dismissed) return
          if (shouldReloadOnVisibilityRegain(awayMs, visibilityReloadAfterMs)) {
            if (debug) {
              console.info('[VersionWatcher] visibility-regain auto-reload', {
                awayMs, threshold: visibilityReloadAfterMs,
              })
            }
            window.location.reload()
          }
        })
      }
    }
    // Initialise hidden timestamp if we mounted while tab was hidden.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      hiddenSinceRef.current = Date.now()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [pollIntervalMs, debug, visibilityReloadAfterMs])

  // Optional: auto-reload after configured idle period.
  useEffect(() => {
    if (!vState.updateAvailable || !autoReloadAfterMs) return
    const onActivity = () => { lastInputAtRef.current = Date.now() }
    window.addEventListener('mousemove', onActivity, { passive: true })
    window.addEventListener('keydown', onActivity, { passive: true })
    window.addEventListener('touchstart', onActivity, { passive: true })
    const interval = window.setInterval(() => {
      if (Date.now() - lastInputAtRef.current >= autoReloadAfterMs) {
        window.location.reload()
      }
    }, 30_000)
    return () => {
      window.removeEventListener('mousemove', onActivity)
      window.removeEventListener('keydown', onActivity)
      window.removeEventListener('touchstart', onActivity)
      window.clearInterval(interval)
    }
  }, [vState.updateAvailable, autoReloadAfterMs])

  // Optional: auto-reload countdown. When a brand-new version is
  // detected we kick off a per-second tick that reloads at zero
  // unless the operator clicks Cancel. Keeps the operator in
  // control while removing the "click Reload now" tax for the
  // common case. Skips entirely when:
  //   - no update is pending
  //   - operator already dismissed the banner
  //   - operator already cancelled THIS version's countdown
  //   - feature is disabled (autoReloadCountdownMs == null)
  useEffect(() => {
    if (autoReloadCountdownMs == null) return
    if (!vState.updateAvailable) return
    if (vState.dismissed) return

    const targetVersion = vState.updateAvailable.nextVersion
    if (countdownStartedForRef.current !== targetVersion) {
      countdownStartedForRef.current = targetVersion
      setCountdown((prev) => startCountdown(prev, autoReloadCountdownMs))
    }

    const TICK_MS = 1000
    const interval = window.setInterval(() => {
      const { next, expired } = tickCountdown(countdownRef.current, TICK_MS)
      if (next !== countdownRef.current) setCountdown(next)
      if (expired) {
        window.clearInterval(interval)
        if (debug) console.info('[VersionWatcher] countdown auto-reload', { targetVersion })
        window.location.reload()
      }
    }, TICK_MS)
    return () => window.clearInterval(interval)
  }, [vState.updateAvailable, vState.dismissed, autoReloadCountdownMs, debug])

  const handleReload = () => {
    // Belt-and-braces: clear common storage flags that could leak
    // stale auth state into the new bundle. We DO keep the tenant
    // dashboard key + sso suppression flag because both must
    // survive the reload to land the user where they were.
    try {
      // Cache API (used by some PWA bundles) — purge all entries.
      if (typeof caches !== 'undefined' && caches.keys) {
        caches.keys().then((keys) => {
          for (const k of keys) void caches.delete(k)
        }).catch(() => { /* ignore */ })
      }
    } catch { /* ignore */ }
    window.location.reload()
  }

  const handleBadgeClick = () => {
    if (!loadedVersion) return
    const text = loadedBuiltAt
      ? `${loadedVersion} (built ${loadedBuiltAt})`
      : loadedVersion
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(text)
      }
    } catch { /* ignore — best-effort */ }
    setBadgeCopied(true)
    window.setTimeout(() => setBadgeCopied(false), 1500)
  }

  // The banner only shows when an update is pending AND the host wants
  // it; the badge is independent and shows whenever the host opts in
  // and we have a loaded version. Both can render together — banner is
  // bottom-centre above the mobile dock, badge is bottom-RIGHT, so they
  // never overlap.
  const updateAvailable = vState.updateAvailable
  const showBanner = updateAvailable && !vState.dismissed && showFloatingBanner
  const showBadge = showVersionBadge && !!loadedVersion
  if (!showBanner && !showBadge) return null

  const countdownActive = countdown.remainingMs != null && !countdown.cancelled
  const countdownLabel = countdownActive ? formatCountdownLabel(countdown.remainingMs) : ''
  const onCancelCountdown = () => {
    setCountdown((prev) => cancelCountdown(prev))
  }

  return (
    <>
      {showBanner && updateAvailable && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-[1000] flex justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px)+var(--mobile-dock-h,0px))] pointer-events-none"
        >
          <div className="pointer-events-auto flex w-full max-w-sm flex-col gap-3 rounded-2xl border border-primary/30 bg-card/95 px-4 py-4 shadow-lg ring-1 ring-primary/20 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300 sm:flex-row sm:items-center">
            <span className="relative flex h-2.5 w-2.5 shrink-0 self-start sm:self-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
            </span>
            <div className="min-w-0 flex-1 text-sm leading-tight">
              <p className="font-medium text-foreground">
                {countdownActive
                  ? `Reloading in ${countdownLabel}`
                  : 'A new version is available'}
              </p>
              <p className="text-xs text-muted-foreground">
                Build <code className="font-mono">{updateAvailable.nextVersion}</code>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 self-stretch sm:self-center">
            <button
              type="button"
              onClick={handleReload}
              className="inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors sm:flex-none sm:py-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Reload now
            </button>
            {countdownActive ? (
              <button
                type="button"
                onClick={onCancelCountdown}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center px-2 py-1 text-[11px] font-medium rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors sm:min-h-0 sm:min-w-0"
                title="Cancel auto-reload (you can still click Reload now)"
                aria-label="Cancel auto-reload"
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setVState((prev) => applyDismissal(prev))}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center p-1 rounded-full text-muted-foreground hover:bg-accent transition-colors sm:min-h-0 sm:min-w-0"
                title="Dismiss (will reappear when the next deploy lands)"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            </div>
          </div>
        </div>
      )}
      {showBadge && (
        <button
          type="button"
          onClick={handleBadgeClick}
          title={
            loadedBuiltAt
              ? `Build ${loadedVersion}\nBuilt ${loadedBuiltAt}\nClick to copy`
              : `Build ${loadedVersion}\nClick to copy`
          }
          aria-label={`Loaded build ${loadedVersion} — click to copy`}
          // Position via inline style instead of bottom-2/right-2
          // tailwind so tenants can lift the badge clear of fixed
          // bottom toolbars (SAM's Kanban view-switcher) or right-
          // side rails. See the bottomOffsetPx / rightOffsetPx
          // props docstrings.
          style={{ bottom: bottomOffsetPx, right: rightOffsetPx }}
          className="fixed z-[999] pointer-events-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono leading-none text-muted-foreground/70 hover:text-foreground bg-background/60 hover:bg-background/90 backdrop-blur-sm border border-border/40 hover:border-border transition-all opacity-60 hover:opacity-100 shadow-sm"
        >
          {badgeCopied ? (
            <>
              <Check className="h-2.5 w-2.5 text-emerald-500" /> copied
            </>
          ) : (
            <>
              <Tag className="h-2.5 w-2.5" /> {loadedVersion}
            </>
          )}
        </button>
      )}
    </>
  )
}

/**
 * Read the current bundle's loaded version, e.g. for diagnostics.
 * Returns ``null`` if the build pre-dates the version stamp plugin.
 */
export function getLoadedAppVersion(): string | null {
  return readMeta(META_VERSION)
}

/**
 * Read the current bundle's built-at timestamp.
 */
export function getLoadedAppBuiltAt(): string | null {
  return readMeta(META_BUILT_AT)
}
